import { formatCurrency, formatMinutes, formatSchoolYear, formatTime, toCamelCase } from "@compass/core";
import DataGrid, {
  Column,
  ColumnChooser,
  Export,
  GroupPanel,
  Grouping,
  Item,
  Pager,
  Paging,
  Summary,
  Toolbar,
  TotalItem,
} from "devextreme-react/data-grid";
import { DataSourceLike } from "devextreme/data/data_source";
import { ExportingEvent } from "devextreme/ui/data_grid";
import {
  ReportPropertyBase,
  ReportPropertySubType,
  ReportPropertySummaryType,
  ReportPropertyType,
} from "generated/openApi";
import { Moment } from "moment";
import moment from "moment-timezone";
import { ReactNode, useEffect, useRef, useState } from "react";
import { DevExpressExportProps } from "./DevExpressExportProps";
import { DevExpressService } from "./DevExpressService";

export const DevExpressReport = (props: {
  dataSource: DataSourceLike<any, any> | null | undefined;
  onExporting?: () => DevExpressExportProps;
  staticPageSize?: number | undefined;
  fullPageSize?: boolean;
  showColumnChooser?: boolean;
  exportFormats?: ("pdf" | "excel" | "csv")[] | undefined;
  children: ReactNode;
  title?: string;
  showHeaderColumns?: boolean;
}) => {
  const showColumnChooser = props.showColumnChooser ?? true;
  let staticPageSize = props.staticPageSize ?? 10;
  if (props.fullPageSize) staticPageSize = Number.MAX_VALUE;

  const dataGridRef = useRef<DataGrid>(null);
  const [visibleColumnsCount, setVisibleColumnsCount] = useState(0);
  const [hiddenColumnsCount, setHiddenColumnsCount] = useState(0);

  useEffect(() => {
    updateHiddenColumnsCount();
  }, [props.dataSource]);

  function updateHiddenColumnsCount() {
    if (dataGridRef.current) {
      const totalColumns = dataGridRef.current.instance.columnCount();
      const visibleColumns = dataGridRef.current.instance.getVisibleColumns().length;
      setHiddenColumnsCount(totalColumns - visibleColumns);
    }
  }

  const onExporting = (e: ExportingEvent<any, any>, dataSource: DataSourceLike<any, any> | null | undefined) => {
    if (props.onExporting) {
      const exportProps = props.onExporting();

      const jsonString = JSON.stringify(dataSource);
      const sizeInMb = new TextEncoder().encode(jsonString).length / 1048576;

      if (e.format === "pdf") {
        if (sizeInMb > 5) return alert("The data is too large to export to PDF. Please try other export methods.");

        DevExpressService.exportPdf(e, exportProps, true);
      }

      if (e.format === "excel") {
        if (sizeInMb > 10) return alert("The data is too large to export to Excel. Please try exporting in CSV.");

        DevExpressService.exportExcel(e, exportProps);
      }

      if (e.format === "csv") {
        DevExpressService.exportCsv(e, exportProps);
      }
    }
  };

  const onToolbarPreparing = (e) => {
    const toolbarItems = e.toolbarOptions.items;

    const exportButton = toolbarItems.find((item) => item.name === "exportButton");

    if (exportButton) {
      exportButton.options.text = "Export";
    }
  };

  return (
    <DataGrid
      ref={dataGridRef}
      sorting={{ mode: "multiple" }}
      onToolbarPreparing={onToolbarPreparing}
      width="100%"
      dataSource={props.dataSource}
      onExporting={(e) => onExporting(e, props.dataSource)}
      showBorders
      wordWrapEnabled
      columnAutoWidth
      allowColumnReordering
      allowColumnResizing
      showRowLines
      onOptionChanged={() => updateHiddenColumnsCount()}
      showColumnHeaders={props.showHeaderColumns ?? true}
    >
      <Paging defaultPageSize={staticPageSize ? staticPageSize : 10} />
      <Pager showPageSizeSelector showNavigationButtons allowedPageSizes={[5, 10, 50, 100]} showInfo={true} />
      <GroupPanel visible={true} />
      <Grouping contextMenuEnabled autoExpandAll={true} />

      {showColumnChooser && <ColumnChooser enabled />}
      {props.exportFormats && props.exportFormats?.length > 0 && <Export enabled formats={props.exportFormats} />}

      <Toolbar>
        {props.title && <Item location="before" cssClass="dx-toolbar-title" text={props.title} />}
        {showColumnChooser && (
          <Item
            widget="dxButton"
            location="after"
            options={{
              icon: "columnchooser",
              text: `Columns (${hiddenColumnsCount})`,
              onClick: () => {
                if (dataGridRef.current) {
                  dataGridRef.current.instance.showColumnChooser();
                }
              },
            }}
          />
        )}
        <Item name="exportButton" />
      </Toolbar>

      {props.children}
    </DataGrid>
  );
};

// this has to be a function instead of a component for DevExpress to work
export const renderReportColumn = (props: {
  property: ReportPropertyBase;
  propertyKey: string;
  timezone?: string;
  customizeText?:
    | ((cellInfo: { groupInterval: string | number; target: string; value: any; valueText: string }) => string)
    | undefined;
  cellRender?: ((...params: any) => ReactNode) | undefined;
}) => {
  var propertyIdentifer = toCamelCase(props.property[props.propertyKey]);

  const getDataType = (
    propertyType: ReportPropertyType
  ): "string" | "number" | "boolean" | "object" | "date" | "datetime" | undefined => {
    switch (propertyType) {
      case ReportPropertyType.String:
        return "string";
      case ReportPropertyType.Int:
      case ReportPropertyType.Decimal:
        return "number";
      case ReportPropertyType.Date:
        return "date";
      case ReportPropertyType.DateTimeOffset:
        return "datetime";
      case ReportPropertyType.Bool:
      case ReportPropertyType.NullableBool:
        return "boolean";
    }

    return undefined;
  };

  const getFormat = (propertyType: ReportPropertyType) => {
    switch (propertyType) {
      case ReportPropertyType.Date:
        return "MM/dd/yyyy";
    }

    return undefined;
  };

  const calculateSortValue = () => {
    if (props.property.displayOrderProperty) {
      return (rowData) => {
        var sortPropertyIdentifer = toCamelCase(props.property.displayOrderProperty![props.propertyKey]);
        var displayOrder = rowData[sortPropertyIdentifer!];
        var sortValue =
          (displayOrder ? String(displayOrder).padStart(10, "0") : "") + (rowData[propertyIdentifer!] ?? "");
        return sortValue;
      };
    }

    return undefined;
  };

  // cellRender vs customizeText:
  // cellRender affects only the grid, not the export .. this is good for things like dates, where raw dates in excel can be sorted correctly
  // customizeText affects both the grid and the export, this is good only for when resulting data is string
  const cellRender = (data: any) => {
    const value = data.value;

    switch (props.property.propertyType) {
      case ReportPropertyType.Decimal:
        if (props.property.propertySubType === ReportPropertySubType.Currency) return formatCurrency(value);
        break;
    }

    return data.text;
  };

  // this returns undefined if no customization is needed, returns a function that matches the signature of customizeText of devExtreme if customization is needed
  const customizeText = () => {
    if (
      (props.property.propertyType === ReportPropertyType.Int &&
        (props.property.propertySubType === ReportPropertySubType.SchoolYear ||
          props.property.propertySubType === ReportPropertySubType.DurationInMinutes)) ||
      props.property.propertyType === ReportPropertyType.DateTimeOffset ||
      props.property.propertyType === ReportPropertyType.TimeSpan
    ) {
      return (cellInfo: { groupInterval: string | number; target: string; value: any; valueText: string }): string => {
        const value = cellInfo.value;

        switch (props.property.propertyType) {
          case ReportPropertyType.Int:
            if (props.property.propertySubType === ReportPropertySubType.SchoolYear)
              return formatSchoolYear(value) ?? "";
            if (props.property.propertySubType === ReportPropertySubType.DurationInMinutes)
              return formatMinutes(value) ?? "";
            break;
          case ReportPropertyType.DateTimeOffset:
            let momentDatetime: Moment = moment(value);
            if (props?.timezone) momentDatetime = momentDatetime.tz(props?.timezone);
            return `${momentDatetime.format("MM/DD/YYYY hh:mm A")} ${momentDatetime.zoneAbbr()}`;
          case ReportPropertyType.TimeSpan:
            return formatTime(value) ?? "";
        }

        // this should never be reached
        return cellInfo.valueText;
      };
    }

    return undefined;
  };

  return (
    <Column
      key={propertyIdentifer}
      dataField={propertyIdentifer}
      caption={props.property.displayName}
      visible={!props.property.hidden}
      width={props.property.width}
      dataType={getDataType(props.property.propertyType)}
      format={getFormat(props.property.propertyType)}
      calculateSortValue={calculateSortValue()}
      customizeText={customizeText()}
      cellRender={cellRender}
      sortIndex={props.property.defaultSort ? props.property.defaultSort?.item1 : undefined}
      sortOrder={props.property.defaultSort ? (props.property.defaultSort?.item2 ? "desc" : "asc") : undefined}
    />
  );
};

type SummaryType =
  | "sum-currency"
  | "sum"
  | "avg"
  | "count"
  | "max"
  | "min"
  | "sum-label"
  | "avg-label"
  | "count-label"
  | "max-label"
  | "min-label";
export const renderReportSummary = (props: {
  properties: ReportPropertyBase[] | undefined;
  propertyKey: string;
  timezone?: string;
  hideLabel?: boolean;
}) => {
  if (!props.properties) return null;

  const aggregateItems = (properties: ReportPropertyBase[], summaryType: SummaryType) => {
    const _totalItems = properties.map((property) => {
      var propertyIdentifer = toCamelCase(property[props.propertyKey]);

      if (
        (summaryType === "sum-currency" && property.summaryTypes?.includes(ReportPropertySummaryType.SumCurrency)) ||
        (summaryType === "sum" && property.summaryTypes?.includes(ReportPropertySummaryType.Sum)) ||
        (summaryType === "avg" && property.summaryTypes?.includes(ReportPropertySummaryType.Avg)) ||
        (summaryType === "count" && property.summaryTypes?.includes(ReportPropertySummaryType.Count)) ||
        (summaryType === "max" && property.summaryTypes?.includes(ReportPropertySummaryType.Max)) ||
        (summaryType === "min" && property.summaryTypes?.includes(ReportPropertySummaryType.Min)) ||
        (summaryType === "sum-label" && property.summaryTypes?.includes(ReportPropertySummaryType.SumLabel)) ||
        (summaryType === "avg-label" && property.summaryTypes?.includes(ReportPropertySummaryType.AvgLabel)) ||
        (summaryType === "count-label" && property.summaryTypes?.includes(ReportPropertySummaryType.CountLabel)) ||
        (summaryType === "max-label" && property.summaryTypes?.includes(ReportPropertySummaryType.MaxLabel)) ||
        (summaryType === "min-label" && property.summaryTypes?.includes(ReportPropertySummaryType.MinLabel))
      ) {
        const isLabel =
          summaryType === "sum-label" ||
          summaryType === "avg-label" ||
          summaryType === "count-label" ||
          summaryType === "max-label" ||
          summaryType === "min-label";

        return (
          <TotalItem
            key={propertyIdentifer}
            column={propertyIdentifer}
            // @ts-ignore
            summaryType={!isLabel ? summaryType==="sum-currency" ? "sum" : summaryType : undefined}
            customizeText={(cellInfo) => {
              var label = "";
              switch (summaryType) {
                case "sum":
                case "sum-currency":
                case "sum-label":
                  label = "Total";
                  break;
                case "avg":
                case "avg-label":
                  label = "Avg";
                  break;
                case "count":
                case "count-label":
                  label = "Count";
                  break;
                case "max":
                case "max-label":
                  label = "Max";
                  break;
                case "min":
                case "min-label":
                  label = "Min";
                  break;
              }

              if (isLabel) return `${label}: `;

              let value = cellInfo.value;

                let numericValue: number;
                if (typeof value === 'number') {
                    numericValue = value;
                } else if (value instanceof Date) {
                    numericValue = value.getTime(); // Convert date to timestamp
                } else {
                    // It's a string, so parse it
                    numericValue = parseFloat(String(value).replace(/[^0-9.-]+/g, ""));
                    if (isNaN(numericValue)) numericValue = 0; // Default to 0 if parsing fails
                }

              if (
                //summaryType === "avg" &&
                property.propertyType === ReportPropertyType.Int ||
                property.propertyType === ReportPropertyType.Decimal
              ) {
                const factor = Math.pow(10, 2);
                  numericValue = Math.floor(numericValue * factor) / factor;
              }

                return props.hideLabel ? String(numericValue) : `${label}: ${summaryType == "sum-currency" ? formatCurrency(numericValue) : String(numericValue)}`;
            }}
          />
        );
      }

      return null;
    });

    if (!_totalItems || _totalItems.length === 0) return null;
    return _totalItems;
  };

  return (
    <Summary>
      {/* Not an elegant way to repeat these, but DevExpress expects JSX output to be in a strict nesting format */}
      {aggregateItems(props.properties, "sum-currency")}
      {aggregateItems(props.properties, "sum")}
      {aggregateItems(props.properties, "avg")}
      {aggregateItems(props.properties, "count")}
      {aggregateItems(props.properties, "max")}
      {aggregateItems(props.properties, "min")}
      {aggregateItems(props.properties, "sum-label")}
      {aggregateItems(props.properties, "avg-label")}
      {aggregateItems(props.properties, "count-label")}
      {aggregateItems(props.properties, "max-label")}
      {aggregateItems(props.properties, "min-label")}
    </Summary>
  );
};
